import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  TableSortLabel,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";

const RoiLeads = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [roiLeads, setRoiLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLeads, setTotalLeads] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const REACT_APP_SUPERADMIN_APP_API_BASE_URL = process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL || "http://localhost:4000";

  const handleSidebarStateChange = (isOpen) => {
    setSidebarOpen(isOpen);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const fetchRoiLeads = async () => {
    setLoading(true);
    try {
      const cacheBuster = `_t=${Date.now()}`;
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const response = await fetch(
        `${REACT_APP_SUPERADMIN_APP_API_BASE_URL}/roi-leads?page=${page + 1}&limit=${rowsPerPage}&sortBy=${sortBy}&sortOrder=${sortOrder}${searchParam}&${cacheBuster}`,
        {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setRoiLeads(data.data.roiLeads);
        setTotalLeads(data.data.total);
      } else {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      showSnackbar("Failed to fetch ROI leads", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoiLeads();
  }, [page, rowsPerPage, sortBy, sortOrder, searchTerm]);

  const handleDeleteLead = async (leadId) => {
    if (window.confirm("Are you sure you want to delete this ROI lead?")) {
      try {
        const response = await fetch(`${REACT_APP_SUPERADMIN_APP_API_BASE_URL}/roi-leads/${leadId}`, {
          method: "DELETE",
        });
        const data = await response.json();
        if (data.success) {
          showSnackbar("ROI lead deleted successfully");
          fetchRoiLeads();
        } else {
          showSnackbar(data.message, "error");
        }
      } catch (error) {
        showSnackbar("Failed to delete ROI lead", "error");
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div>
      <Sidebar onSidebarStateChange={handleSidebarStateChange} />
      <Navbar sidebarOpen={sidebarOpen} />
      
      <Box 
        sx={{ 
          ml: sidebarOpen ? "220px" : "100px", 
          p: { xs: 2, sm: 3, md: 4, xl: 4 },
          backgroundColor: "#fafafa",
          minHeight: "100vh",
          transition: "margin-left 0.3s ease",
          fontFamily: "Outfit"
        }}
      >
        {/* Header Section */}
        <Box sx={{ marginBottom: "32px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <h1
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#2B3674",
                textAlign: "left",
                margin: "0 0 8px 0",
                lineHeight: "1.4",
                fontFamily: "Outfit",
              }}
            >
              ROI Leads
            </h1>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchRoiLeads}
              disabled={loading}
              sx={{ 
                borderColor: "#9048FB", 
                color: "#9048FB",
                "&:hover": { 
                  borderColor: "#7c3aed",
                  backgroundColor: "#f3e8ff"
                },
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 3,
                py: 1.5,
                fontFamily: "Outfit"
              }}
            >
              Refresh
            </Button>
          </Box>
          <p
            style={{
              color: "#A3AED0",
              fontSize: "14px",
              margin: 0,
              textAlign: "left",
              fontWeight: 400,
              fontFamily: "Outfit",
            }}
          >
            View and manage all ROI leads from your landing pages
          </p>
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by name, email, or website..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#A3AED0" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                fontFamily: "Outfit",
                backgroundColor: "#ffffff",
                "& fieldset": {
                  borderColor: "#e5e7eb",
                },
                "&:hover fieldset": {
                  borderColor: "#9048FB",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#9048FB",
                },
              },
              "& .MuiInputBase-input": {
                fontFamily: "Outfit",
                fontSize: "14px",
                color: "#374151",
                "&::placeholder": {
                  color: "#A3AED0",
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* Main Content Card */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: { xs: "16px", sm: "24px", md: "32px", xl: "40px" },
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            border: "1px solid #f1f5f9",
          }}
        >
          <TableContainer 
            component={Paper}
            sx={{
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              boxShadow: "none",
              "& .MuiTable-root": {
                borderCollapse: "separate",
                borderSpacing: 0,
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f8fafc",
                    "& .MuiTableCell-head": {
                      backgroundColor: "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#374151",
                      fontFamily: "Outfit",
                      py: 2,
                    }
                  }}
                >
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "name"}
                      direction={sortBy === "name" ? sortOrder : "asc"}
                      onClick={() => handleSort("name")}
                      sx={{
                        fontFamily: "Outfit",
                        fontWeight: 600,
                        color: "#374151",
                        "&.Mui-active": {
                          color: "#2B3674",
                        },
                        "&:hover": {
                          color: "#2B3674",
                        }
                      }}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>
                    Business Email
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>
                    Business Website
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "createdAt"}
                      direction={sortBy === "createdAt" ? sortOrder : "asc"}
                      onClick={() => handleSort("createdAt")}
                      sx={{
                        fontFamily: "Outfit",
                        fontWeight: 600,
                        color: "#374151",
                        "&.Mui-active": {
                          color: "#2B3674",
                        },
                        "&:hover": {
                          color: "#2B3674",
                        }
                      }}
                    >
                      Created At
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>
                    Timestamp
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: "center", py: 8 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <CircularProgress sx={{ color: "#9048FB" }} />
                        <Typography sx={{ fontFamily: "Outfit", color: "#6b7280" }}>
                          Loading ROI leads...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : roiLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: "center", py: 8 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            backgroundColor: "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid #e5e7eb",
                          }}
                        >
                          <SearchIcon sx={{ fontSize: 40, color: "#9ca3af" }} />
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: "Outfit",
                              fontWeight: 600,
                              color: "#374151",
                              mb: 1,
                            }}
                          >
                            No ROI leads found
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "Outfit",
                              color: "#6b7280",
                              mb: 3,
                              maxWidth: 400,
                            }}
                          >
                            {searchTerm ? "No leads match your search criteria. Try adjusting your search terms." : "ROI leads will appear here when users submit information through your landing pages."}
                          </Typography>
                          {searchTerm && (
                            <Button
                              variant="outlined"
                              onClick={() => setSearchTerm("")}
                              sx={{
                                borderColor: "#9048FB",
                                color: "#9048FB",
                                "&:hover": {
                                  borderColor: "#7c3aed",
                                  backgroundColor: "#f3e8ff"
                                },
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "8px",
                                px: 3,
                                py: 1.5,
                                fontFamily: "Outfit",
                              }}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  roiLeads.map((lead, index) => (
                    <TableRow 
                      key={lead._id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                        },
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid #f1f5f9",
                          fontFamily: "Outfit",
                          fontSize: "14px",
                          color: "#374151",
                          py: 2,
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500, color: "#2B3674" }}>
                        {lead.name}
                      </TableCell>
                      <TableCell sx={{ color: "#6b7280" }}>
                        {lead.businessEmail}
                      </TableCell>
                      <TableCell sx={{ color: "#6b7280" }}>
                        <a 
                          href={lead.businessWebsite} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            color: "#9048FB", 
                            textDecoration: "none",
                            fontFamily: "Outfit"
                          }}
                        >
                          {lead.businessWebsite}
                        </a>
                      </TableCell>
                      <TableCell sx={{ color: "#6b7280" }}>
                        {formatDate(lead.createdAt)}
                      </TableCell>
                      <TableCell sx={{ color: "#6b7280" }}>
                        {formatTimestamp(lead.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteLead(lead._id)}
                            sx={{
                              color: "#ef4444",
                              "&:hover": {
                                backgroundColor: "#fee2e2",
                                color: "#dc2626",
                              },
                              borderRadius: "6px",
                              p: 0.5,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {roiLeads.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalLeads}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid #f1f5f9",
                mt: 2,
                "& .MuiTablePagination-toolbar": {
                  fontFamily: "Outfit",
                  color: "#374151",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontFamily: "Outfit",
                  fontSize: "14px",
                  color: "#6b7280",
                },
                "& .MuiTablePagination-select": {
                  fontFamily: "Outfit",
                  fontSize: "14px",
                  color: "#374151",
                }
              }}
            />
          )}
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            borderRadius: "12px",
            fontFamily: "Outfit",
            fontWeight: 500,
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            borderRadius: "12px",
            fontFamily: "Outfit",
            fontWeight: 500,
            "& .MuiAlert-message": {
              fontFamily: "Outfit",
            },
            "& .MuiAlert-icon": {
              fontSize: "20px",
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default RoiLeads;
